import posthog from "posthog-js";
import { useUserPlanQuery } from "@/features/users/api/user-plan-options";
import { ResumeSubscriptionForm } from "@/features/users/components/resume-subscription-form";
import UpgradeForm from "@/features/users/components/upgrade-form";
import usePricingModal, {
  PAYWALL_TYPES,
  type PaywallType,
} from "@/lib/hooks/use-pricing-modal";
import { useI18n } from "@/locales/client";
import { useAuth } from "../providers/auth-context";
import { ResponsiveModal } from "../responsive-modal";

const PRICING_VARIANTS = {
  default: {
    title: "upgradeModal.title",
    description: "upgradeModal.description",
  },
  aiTutorChats: {
    title: "upgradeModal.aiTutorChats.title",
    description: "upgradeModal.aiTutorChats.description",
  },
  fileSize: {
    title: "upgradeModal.fileSize.title",
    description: "upgradeModal.fileSize.description",
  },
  fileUploadCount: {
    title: "upgradeModal.fileUploadCount.title",
    description: "upgradeModal.fileUploadCount.description",
  },
  aiNotesDuration: {
    title: "upgradeModal.aiNotesDuration.title",
    description: "upgradeModal.aiNotesDuration.description",
  },
  aiNotesCount: {
    title: "upgradeModal.aiNotesCount.title",
    description: "upgradeModal.aiNotesCount.description",
  },
  audioLesson: {
    title: "upgradeModal.audioLesson.title",
    description: "upgradeModal.audioLesson.description",
  },
  voiceTutor: {
    title: "upgradeModal.voiceTutor.title",
    description: "upgradeModal.voiceTutor.description",
  },
} as const;

export default function UpgradeModal() {
  const { isOpen, onClose, upgradeReason, paywallType } = usePricingModal();
  const t = useI18n();
  const { userId } = useAuth();
  const { data: planData } = useUserPlanQuery();

  const isPaused = planData?.isPaused ?? false;

  const getVariant = (type?: PaywallType): keyof typeof PRICING_VARIANTS => {
    if (type === PAYWALL_TYPES.AI_TUTOR) return "aiTutorChats";
    if (type === PAYWALL_TYPES.FILE_UPLOAD_EXCEEDS_SIZE_LIMIT)
      return "fileSize";
    if (type === PAYWALL_TYPES.FILE_UPLOAD) return "fileUploadCount";
    if (type === PAYWALL_TYPES.AI_NOTES_DURATION) return "aiNotesDuration";
    if (type === PAYWALL_TYPES.AI_NOTES_COUNT) return "aiNotesCount";
    if (type === PAYWALL_TYPES.AUDIO_LESSON) return "audioLesson";
    if (type === PAYWALL_TYPES.VOICE_TUTOR) return "voiceTutor";
    return "default";
  };

  const variant = getVariant(paywallType);
  const variantConfig = PRICING_VARIANTS[variant];

  const title = isPaused
    ? t("upgradeModal.paused.title")
    : t(variantConfig.title);
  const description = isPaused
    ? t("upgradeModal.paused.description")
    : t(variantConfig.description);

  const handleClose = () => {
    onClose();
    if (paywallType) {
      posthog.capture("closed_upgrade_modal", {
        userId,
        upgradeReason,
        description,
        paywallType,
      });
    }
  };

  return (
    <ResponsiveModal
      title={title}
      titleClassName="text-xl md:text-2xl"
      description={description}
      form={
        isPaused ? (
          <ResumeSubscriptionForm onClose={handleClose} />
        ) : (
          <UpgradeForm />
        )
      }
      open={isOpen}
      onOpenChange={handleClose}
    />
  );
}
